import { NextPage } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { ReactNode, useState } from 'react';
import inputGrid from '../../styles/input-grid.module.css';
import Password from '../inputs/password';
import Layout from '../layout';
import LoginContainer from './container';

/**
 * Creates a Next.js login page asking for an email and password.
 * @param pageTitle
 * @param role Header shows "Log in as {role}"
 * @param endpoint API endpoint to POST to when making a session
 * @param defaultPageAfterLogin Route to go to after logging in if continue url param not set
 * @param blurb Short description of why new users may want to sign up for an account
 * @param createAccountLink
 */
const createEmailPasswordLoginPage =
  (
    pageTitle: string,
    role: string,
    endpoint: string,
    defaultPageAfterLogin: string,
    blurb: ReactNode,
    createAccountLink: string
  ): NextPage =>
  () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const router = useRouter();
    return (
      <>
        <Head>
          <title>{pageTitle}</title>
        </Head>
        <Layout>
          <LoginContainer
            header={pageTitle}
            endpoint={endpoint}
            loginData={{ email, password }}
            onLogin={() => {
              const redirect = new URL(window.location.href).searchParams.get('continue');
              router.push({ pathname: redirect ?? defaultPageAfterLogin });
            }}
          >
            <div className='mb-2'>
              <div className={inputGrid.inputGrid}>
                <input
                  type='email'
                  placeholder='Email'
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
                <Password
                  placeholder='Password'
                  required
                  value={password}
                  isNew={false}
                  onChange={e => setPassword(e.target.value)}
                />
              </div>
            </div>
            <div className='links-container'>
              <wbr />
              <Link href='/password-reset' as={`/password-reset?role=${role}`}>
                <a>Reset password</a>
              </Link>
              <wbr />
              <Link href='/verify-email' as={`/verify-email?role=${role}`}>
                <a>Re-verify email</a>
              </Link>
            </div>
            <style jsx>{`
              .links-container {
                @apply select-none;
              }
              .links-container a {
                @apply mr-4 whitespace-nowrap;
              }
            `}</style>
          </LoginContainer>
          <div className='max-w-3xl -mx-4 sm:mx-auto'>
            <h1 className='text-xl'>New Here?</h1>
            {blurb}
            <Link href={createAccountLink}>
              <a className='button primary hover:text-white cursor-default mt-2'>Create account</a>
            </Link>
          </div>
        </Layout>
      </>
    );
  };

export default createEmailPasswordLoginPage;
